import { SESSION_TIMEOUT } from '@/lib/constants'
import { destroyCookie, setCookie } from 'nookies'
import { destroyFreeTrialId } from '@/utils/freeTrialUtil'
import { destroyUtmCampaign } from '@/utils/utmCampaignUtil'
import { CASE_MANAGEMENT_COOKIE } from '@/utils/landingRouteUtil'
import { destroyLocaleCookie } from '@/utils/cookieUtil'

// JWT payloads are base64url, which `atob` does not accept as-is: `-` and `_`
// have to be mapped back and the padding restored.
export const decode = token => {
  const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
  return JSON.parse(atob(payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')))
}

export const getBearerToken = () => {
  const token = JSON.parse(localStorage.getItem('token'))
  return token
}

export const getRefreshToken = () => JSON.parse(localStorage.getItem('refreshToken'))

export const getUser = () => decode(getBearerToken())

// Which Customer the stored bearer token actually claims. Onboarding needs this
// because the token is minted before the Customer exists: until it carries the
// claim, every customer-scoped request comes back 403.
export const getBearerCustomerId = () => {
  try {
    return getUser()?.customerId ?? null
  } catch {
    return null
  }
}

export const storeTransactionFilterParams = params => {
  localStorage.setItem('transactionFilterParams', JSON.stringify(params))
}

export const getTransactionFilterParams = () => JSON.parse(localStorage.getItem('transactionFilterParams'))

export const storeCasesFilterParams = params => {
  localStorage.setItem('casesFilterParams', JSON.stringify(params))
}

export const getCasesFilterParams = () => JSON.parse(localStorage.getItem('casesFilterParams'))

export const storeTokens = ({ bearerToken, refreshToken }) => {
  localStorage.setItem('token', JSON.stringify(bearerToken))
  localStorage.setItem('refreshToken', JSON.stringify(refreshToken))
}

export const removeTokensFromStorage = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('userProfile')
  localStorage.removeItem('workflowSummary')
  localStorage.removeItem('transactionFilterParams')
  destroyCookie({}, CASE_MANAGEMENT_COOKIE, { path: '/' })
  destroyLocaleCookie()
  // A free trial deep link belongs to the session that opened it — leaving it
  // behind would re-arm the banner for whoever logs in on this machine next, and its
  // UTM parameters would attribute their activity to the previous user's campaign.
  destroyFreeTrialId()
  destroyUtmCampaign()
}

export const storeUserProfile = profile => {
  // Written as an explicit string: a raw `false` is serialised into the header as the
  // literal "false", which every truthiness check then reads as on (sc-9636). Readers
  // go through `landingRouteUtil`, which only accepts "true".
  const caseManagement = profile?.customer?.modules?.caseManagement ? 'true' : 'false'

  setCookie({}, CASE_MANAGEMENT_COOKIE, caseManagement, {
    maxAge: SESSION_TIMEOUT,
    path: '/',
    secure: true,
    sameSite: 'lax',
  })
  localStorage.setItem('userProfile', JSON.stringify(profile))
}

export const getUserProfile = () => JSON.parse(localStorage.getItem('userProfile'))

export const storeWorkflowSummary = summary => {
  localStorage.setItem('workflowSummary', JSON.stringify(summary))
}

export const getWorkflowSummary = () => JSON.parse(localStorage.getItem('workflowSummary'))
